/**
 * Hardcoded company list for the External Transfer node's "Transfer Company" dropdown.
 *
 * This list is intentionally maintained in code: updating it requires a code change
 * and deploy, which is by design (changes are expected to be rare). The list is
 * provided by the customer-front-desk team.
 *
 * `value` is what gets persisted to the switchboard config (legacy `transfer_company`
 * html field) and is read directly off the DB for reporting, so it should be the
 * human-readable company name itself (value === label). Keep values stable once
 * shipped, since existing switchboards store the text, not an id.
 *
 * This is report-only metadata: the value does not affect call routing.
 */
export interface TransferCompanyOption {
  value: string
  label: string
}

// Each entry uses the company name as both value and label. Declaration order
// here does not matter — the exported list is sorted alphabetically below.
const TRANSFER_COMPANY_NAMES = [
  'Doctor Phone',
  'Staff Phone',
  'MARGO',
  'Answer MTI',
  'Kolaxo CCS',
  'Towne Answering',
  'ADAM Services',
  'TelMed Inc',
  'MedConnect USA',
  'NotifyMD',
  'SMC',
  'Rhonda',
  'Arini',
  'Powervox',
  'Intelepeer',
  'Voice Care AI',
  'Enamly',
  'Call Rail',
  'Support DDS',
  'Reach',
  'Dentia.AI',
  'Call Sara',
  'Heidi',
  'Get Viva',
  'Annie',
  '2nd Location',
  'Easton Centre Location',
  'California SW',
  'Ava AI',
  'Call Tracking Metrics',
  'Moneypenny',
  'Go High Level',
  'Orbit Online',
  'Blue Monster Management',
  'Concierge Contact Center',
  'Alex AI',
  'Yobi AI',
  'Sally AI',
  'Golden Goose',
  'E-Assist Billing',
  'UDS',
  'Go - To',
  'Cassandra AI',
  'Broccoli Answering Service',
  'Angel Answering Service',
  'Patient Prism',
  'TrackStat',
  'LeadSigma AI',
  'Jade AI',
  'Fiona AI',
  'Smile Suite',
  'Professional Answering Service',
  'Hello Annie',
  'Rondah AI',
  'Answer Hero',
  'Vet Wise',
  'Myla',
  'Dingaling Answering Service',
  'A Better Answer',
  'Unique Dental',
  'AnswerLink',
  'Well Received',
  'Lex Reception',
  'Southern Voices',
  'Answer Plus',
  'Jane',
  'Kickstart',
  'Answer Midwest',
  'Blazeo',
  'Legwork',
  'Plumbline',
  'Vivian AI',
  'PeerLogic',
  'Guardian Vet',
  'Crtx AI',
  'DSS Billing',
  'Liine AI',
  'Hey Rosie',
  'Unknown AI',
  'Sideline Emergency App',
  'Exotic Vet Emergency Group',
  'Link AI Assistants',
  'Leap Answering Service',
  "Bloomberg Digital's AI",
  'A1 Answering Service',
  'Global Response',
  'InTouch',
  'Posh Answering Service',
  'PracticePilot AI',
  'PatientHub',
  'Vet Triage Service',
  'Dental Intel AI',
  'Arnie AI',
  'ResMax',
  'Alliance Communications',
  'AB Universal Messaging',
  'Southcoast Answering Service',
  'Answer First',
  'Echo Route',
  'Ultimate Phone Concierge',
  'Praxis AI',
  'Donna AI',
  'Amy AI',
  'Chloe Dental AI',
  'Kate AI',
  'Jenny AI',
  'ProTouch Communications',
  'Around the Clock',
  'Answering Service Care',
  '10X Dental',
  'Brilo AI',
  'Solara AI',
  'Medical Exchange Answering Service',
  'AiroDental',
  'Advantage Answering',
  'Neurality Health AI',
  'Savvy Agents AI',
  'Zapier AI',
  'Leap Dental Marketing',
  'Newton AI',
  'Flossie AI',
  'Spot On Schedulers',
  'MAP Communications',
  'Wisdom - Dental Billing',
  'DearDoc AI',
  'Aloha AI',
  'WrightChat',
  'Business Connections',
  'Xonark AI',
  'Trackable AI',
  'PbN AI',
  'SonaDent AI',
  'Perfect Serve',
  'RevenueWell',
  'Twelve AI',
  'Dentistry Support',
  'Retell AI',
  'All-in-One AI',
  'Abby Connect',
  'NR Medical',
  'Enviro Link',
  'Marea AI',
  'AloAI',
  'Zoho CRM',
  'Cubby AI',
  'OpenTech Answering Service',
  'Front Office Solutions',
  'Confido Health AI',
  'PATLive',
  'Central Communications',
  'Resonate AI',
  'Answerphone',
  'AnswerConnect',
  'DentalBase AI',
  'Access Point',
  'Teem Answering Service',
  'Telephone Answering Exchange',
  'Answering Tampa Bay',
  'GetHelpdesk.AI',
  'Veterinary Emergency Clinic',
  'Vernon Pediatric Dentistry',
  'Naptown Smiles',
  'Night and Day Dental Emergency',
  'Local Hospital',
  'McGrath Dental & Implant Clinic',
  'Valley Central Referral & Emergency Center',
  'Eclipse Specialty & Emergency Pet Care',
  'Eastern Pennsylvania Veterinary Emergency Center',
  'Always Answer',
  'Dently.ai',
  'Clara AI',
  'MedSpeak AI',
  'Dentina AI',
  'WebMD',
  'HLSA SecOps',
  'Ruby',
]

export const TRANSFER_COMPANY_OPTIONS: TransferCompanyOption[] =
  TRANSFER_COMPANY_NAMES.map((name) => ({ value: name, label: name })).sort(
    (a, b) => a.label.localeCompare(b.label),
  )
